/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { GanttProps, GanttRowData, GanttTaskData } from '../gantt.types';
import { useUser } from '../../hooks/UNSAFE_useUser';
import { useCssVars } from '../../hooks/PRIVATE_useCssVars';
import { useTextDimensions } from '../../hooks/PRIVATE_useTextDimensions';
import {
  getResolvedTimeAxisProps,
  getUnresolvedTimeAxesLayout,
  getResolvedTimeAxesLayout,
  getTimeAxesZoomParameters
} from '../../PRIVATE_TimeComponent/utils/timeAxisLayoutUtils';
import { getParsedTimeAxisParams } from '../../PRIVATE_TimeComponent/utils/validationUtils';
import { getISOString } from '../../PRIVATE_TimeComponent/utils/timeUtils';
import {
  ganttUnresolvedVars,
  getResolvedRowAxisVars,
  getResolvedRowVars,
  getResolvedTaskVars
} from '../utils/ganttStyleUtils';
import {
  timeComponentUnresolvedVars,
  getResolvedTimeAxisVars
} from '../../PRIVATE_TimeComponent/utils/timeComponentStyleUtils';
import { getPanZoomCanvasLayout } from '../utils/panZoomCanvasLayoutUtils';
import { getWithRowAxisLayout } from '../utils/rowAxisLayoutUtils';
import { getResolvedViewportLayout, getRowDataLayout } from '../utils/dataLayoutUtils';
import {
  getResolvedHorizontalScrollbarLayout,
  getResolvedVerticalScrollbarLayout
} from '../../PRIVATE_TimeComponent/utils/scrollbarLayoutUtils';
import { defaultRowAxisLabelRender } from '../utils/rowAxisUtils';

const cssVars = timeComponentUnresolvedVars.concat(ganttUnresolvedVars);

/**
 * Hook that computes layout related information for the Gantt.
 */
const useGanttLayout = <K1, K2, D1 extends GanttRowData<K1, D1, D2>, D2 extends GanttTaskData<K2>>({
  rowAxis: rowAxisProps,
  gridlines: { horizontal: gridlinesHorizontal = 'off', vertical: gridlinesVertical = 'on' } = {
    horizontal: 'off',
    vertical: 'on'
  },
  axisPosition = 'top',
  minorAxis,
  majorAxis,
  start,
  end,
  viewportStart,
  viewportEnd,
  onViewportChange,
  scrollPosition: { y: scrollPosition = 0 } = { y: 0 },
  onScrollPositionChange,
  rows,
  selectedIds,
  selectionMode = 'none'
}: Omit<GanttProps<K2, D1, D2>, 'width' | 'height'>) => {
  const { direction } = useUser();
  const { resolvedVars, cssContent } = useCssVars(cssVars);
  const { textMeasureContent, getTextDimensions } = useTextDimensions();

  const isRTL = direction === 'rtl';
  const layoutContent = (
    <>
      {textMeasureContent}
      {cssContent}
    </>
  );

  const {
    startTime,
    endTime,
    viewportStartTime,
    viewportEndTime,
    isValidTimeConfig: isValidConfig
  } = getParsedTimeAxisParams(start, end, viewportStart, viewportEnd);

  // TODO: take a look a useCssVars and see if we can tighten the types more and make the following check nicer.
  if (!getTextDimensions || Object.keys(resolvedVars).length === 0) {
    return { isRTL, layoutContent, isValidConfig, isReady: false };
  }

  if (!isValidConfig) {
    return { isRTL, layoutContent, isValidConfig, isReady: true };
  }

  // Resolve row axis props
  const rowAxisRendered = rowAxisProps === undefined ? 'off' : 'on';
  const rowAxis = rowAxisProps === 'on' ? {} : rowAxisProps;
  const rowAxisWidth = rowAxis?.width ?? 'max-content';
  const rowAxisMaxWidth = rowAxis?.maxWidth ?? 'none';
  const rowAxisLabelRender = rowAxis?.labelRenderer ?? defaultRowAxisLabelRender;

  // General layout
  const unresolvedTimeAxesLayout = getUnresolvedTimeAxesLayout(axisPosition, minorAxis, majorAxis);
  const panZoomLayout = getPanZoomCanvasLayout(unresolvedTimeAxesLayout);
  const withRowAxisLayout =
    rowAxisRendered === 'on'
      ? getWithRowAxisLayout<D1>(unresolvedTimeAxesLayout, {
          width: rowAxisWidth,
          maxWidth: rowAxisMaxWidth,
          label: rowAxisLabelRender
        })
      : undefined;

  // Time axes layout and Zoom parameters
  const timeAxisResolvedVars = getResolvedTimeAxisVars(resolvedVars);
  const resolvedMajorAxisProps =
    majorAxis && getResolvedTimeAxisProps(timeAxisResolvedVars.defaultTimeAxisHeight, majorAxis);
  const resolvedMinorAxisProps = getResolvedTimeAxisProps(
    timeAxisResolvedVars.defaultTimeAxisHeight,
    minorAxis
  );
  // TODO: see if we need to useMemo this
  const zoomParameters = getTimeAxesZoomParameters(
    getTextDimensions,
    timeAxisResolvedVars.timeAxisLabelPaddingInlineStart,
    timeAxisResolvedVars.timeAxisLabelPaddingInlineEnd,
    timeAxisResolvedVars.timeAxisLabelMinWidth,
    timeAxisResolvedVars.minorAxisFont,
    startTime,
    endTime,
    resolvedMinorAxisProps.zoomOrder
  );
  const getTimeAxesLayout = getResolvedTimeAxesLayout(
    getTextDimensions,
    timeAxisResolvedVars.timeAxisLabelPaddingInlineStart,
    timeAxisResolvedVars.timeAxisLabelPaddingInlineEnd,
    timeAxisResolvedVars.majorAxisFont,
    timeAxisResolvedVars.minorAxisFont,
    zoomParameters.getZoomOrderViewDurationThresholds,
    axisPosition,
    startTime,
    endTime,
    isRTL,
    resolvedMinorAxisProps,
    resolvedMajorAxisProps,
    viewportStartTime,
    viewportEndTime
  );

  // Row data layout
  const rowResolvedVars = getResolvedRowVars(resolvedVars);
  const taskResolvedVars = getResolvedTaskVars(resolvedVars);
  const gridlines = { horizontal: gridlinesHorizontal, vertical: gridlinesVertical };
  const rowDataLayout = getRowDataLayout<K1, K2, D1, D2>(
    rows,
    { ...rowResolvedVars, ...taskResolvedVars },
    gridlines,
    { selectedIds, selectionMode }
  );

  // Row axis layout
  const rowAxisResolvedVars = getResolvedRowAxisVars(resolvedVars);
  const rowAxisLayout = {
    labelPaddingInlineStart: rowAxisResolvedVars.rowLabelPaddingInlineStart,
    labelPaddingInlineEnd: rowAxisResolvedVars.rowLabelPaddingInlineEnd,
    rowData: rowDataLayout.rowData,
    rowObjs: rowDataLayout.rowObjs,
    idRowObjMap: rowDataLayout.idRowObjMap,
    isHorizontalGridlinesVisible: gridlinesHorizontal === 'on'
  };

  // Horizontal scrollbar layout
  const getHorizontalScrollbarLayout = getResolvedHorizontalScrollbarLayout(
    viewportStartTime ?? startTime,
    viewportEndTime ?? endTime,
    startTime,
    endTime,
    isRTL
  );

  const onHorizontalScrollChange = (newViewStartTime: number, newViewEndTime: number) => {
    onViewportChange?.({
      viewportStart: getISOString(newViewStartTime),
      viewportEnd: getISOString(newViewEndTime),
      majorAxisScale: majorAxis?.scale,
      minorAxisScale: minorAxis.scale
    });
  };

  // Vertical scrollbar layout
  const getVerticalScrollbarLayout = getResolvedVerticalScrollbarLayout(
    scrollPosition,
    undefined,
    0,
    rowDataLayout.totalRowsHeight
  );

  const onVerticalScrollChange = (newScrollPosition: number) => {
    onScrollPositionChange?.({ y: newScrollPosition });
  };

  return {
    scrollPosition,
    panZoomLayout,
    withRowAxisLayout,
    getTimeAxesLayout,
    zoomParameters:
      viewportStartTime !== undefined && viewportEndTime !== undefined
        ? {
            viewportStartTime,
            viewportEndTime,
            minorAxisZoomOrderIndex: resolvedMinorAxisProps.zoomOrderIndex,
            majorAxisZoomOrder: resolvedMajorAxisProps?.zoomOrder,
            majorAxisZoomOrderIndex: resolvedMajorAxisProps?.zoomOrderIndex,
            ...zoomParameters
          }
        : undefined,
    rowDataLayout,
    rowAxisLayout,
    getResolvedViewportLayout,
    getHorizontalScrollbarLayout,
    onHorizontalScrollChange,
    getVerticalScrollbarLayout,
    onVerticalScrollChange,
    isRTL,
    layoutContent,
    isValidConfig,
    isReady: true
  };
};

export { useGanttLayout };
